import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Users, ArrowLeft, ExternalLink, Bug, Newspaper, Flag } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

interface ActorCves {
  actor: { id: string; name: string; sponsor: string; category: string;
           aliases: string[]; software: string[] } | null
  matched_via: string[]
  total: number
  structured_count: number
  cves: any[]
}

const CATEGORY_TONE: Record<string, string> = {
  'nation-state': 'bg-rose/15 text-rose-light border-rose/40',
  'ransomware':   'bg-purple-500/15 text-purple-300 border-purple-500/40',
  'criminal':     'bg-amber/15 text-amber-light border-amber/40',
  'hacktivist':   'bg-blue/15 text-blue-light border-blue/40',
  'unknown':      'bg-surface-raised text-text-muted border-border',
}

export default function ActorDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [data, setData] = useState<ActorCves | null>(null)
  const [news, setNews] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id) return
    setLoading(true)
    Promise.all([
      api.get(`/api/cyber/threat-actors/${encodeURIComponent(id)}/cves`, { params: { limit: 60 } }),
      api.get('/api/cyber/brand-mentions', { params: { brand: id, days: 60, limit: 30 } }).catch(() => ({ data: { mentions: [] } })),
    ])
      .then(([cveR, newsR]) => {
        setData(cveR.data)
        setNews(newsR.data?.mentions || [])
      })
      .finally(() => setLoading(false))
  }, [id])

  if (loading) return <div className="card p-6 text-center text-text-muted text-sm">Loading actor…</div>
  if (!data?.actor) return <div className="card p-6 text-center text-text-muted text-sm">Actor not found.</div>

  const a = data.actor
  return (
    <div className="space-y-4 animate-fade-in">
      <Link to="/app/threat-actors" className="text-text-muted hover:text-text-primary text-[11px] inline-flex items-center gap-1">
        <ArrowLeft size={12} /> All threat actors
      </Link>

      <div className="card p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
              <Users size={24} className="text-rose" /> {a.name}
            </h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={clsx('px-2 py-0.5 rounded border text-[10px] uppercase font-mono font-semibold', CATEGORY_TONE[a.category] || CATEGORY_TONE.unknown)}>
                {a.category}
              </span>
              <span className="px-2 py-0.5 rounded border border-border text-[10px] flex items-center gap-1">
                <Flag size={10} /> {a.sponsor}
              </span>
            </div>
          </div>
        </div>
        {a.aliases.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Aliases ({a.aliases.length})</div>
            <div className="flex flex-wrap gap-1 mt-1">
              {a.aliases.map(al => (
                <span key={al} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-surface-raised border border-border">
                  {al}
                </span>
              ))}
            </div>
          </div>
        )}
        {a.software.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Tooling / malware ({a.software.length})</div>
            <div className="flex flex-wrap gap-1 mt-1">
              {a.software.slice(0, 30).map(s => (
                <span key={s} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300 border border-purple-500/30">
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
            <Bug size={14} className="text-rose" /> Linked CVEs
            <span className="text-[10px] text-text-muted font-normal">
              {data.total} found ({data.structured_count} MITRE-curated)
            </span>
          </h2>
          {data.cves.length === 0 ? (
            <p className="text-[11px] text-text-muted italic">No CVE attributions yet.</p>
          ) : (
            <div className="space-y-1 max-h-[480px] overflow-y-auto pr-1">
              {data.cves.slice(0, 30).map((c: any) => (
                <Link key={c.cve_id} to={`/app/cve/${encodeURIComponent(c.cve_id)}`}
                  className="block p-2 rounded border border-border hover:bg-surface-hover text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-blue-light">{c.cve_id}</span>
                    <div className="flex items-center gap-1">
                      {c.structured && <span className="text-[8px] text-emerald-light">✓ MITRE</span>}
                      {c.exploited_in_wild && <span className="badge-red text-[8px]">KEV</span>}
                      {c.cvss_score && <span className="text-[10px] font-mono text-text-muted">{c.cvss_score.toFixed(1)}</span>}
                    </div>
                  </div>
                  <div className="text-[10px] text-text-muted line-clamp-1">{c.summary}</div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="card p-4">
          <h2 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
            <Newspaper size={14} className="text-cyan" /> Recent news mentions
            <span className="text-[10px] text-text-muted font-normal">last 60 days</span>
          </h2>
          {news.length === 0 ? (
            <p className="text-[11px] text-text-muted italic">No news mentions in the corpus yet.</p>
          ) : (
            <div className="space-y-1 max-h-[480px] overflow-y-auto pr-1">
              {news.slice(0, 30).map((n: any) => (
                <a key={n.id} href={n.url} target="_blank" rel="noreferrer noopener"
                  className="block p-2 rounded border border-border hover:bg-surface-hover text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-mono text-blue-light truncate">{n.source}</span>
                    {n.published_at && (
                      <span className="text-[10px] text-text-muted whitespace-nowrap">
                        {formatDistanceToNow(new Date(n.published_at), { addSuffix: true })}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-text-primary line-clamp-2">{n.title}</div>
                  {(n.cves?.length > 0 || n.actors?.length > 0) && (
                    <div className="flex flex-wrap gap-1 mt-1 text-[9px]">
                      {(n.cves || []).slice(0, 3).map((c: string) => (
                        <span key={c} className="font-mono px-1 py-0.5 rounded bg-rose/10 text-rose-light">{c}</span>
                      ))}
                    </div>
                  )}
                  <ExternalLink size={9} className="text-text-muted mt-1" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
